import React, { useState } from 'react';
import { ModelVersionList } from 'src/components/ModelVersionList';
import { ProfileDetails } from 'src/components/ProfileDetails';
import { useNavigate, useParams } from 'react-router-dom';
import { getApplication } from 'src/hooks/useApplications';
import { ApplicationModal } from 'src/components/ApplicationModal';
import { Placeholder } from 'src/components/Placeholder';
import { getRoleName } from 'src/utils/generalUtils';
import { PermissionAwareButtonDropdown } from 'src/components/PermissionAwareButtonDropdown';
import {
  ButtonDropdownProps,
  Container,
  KeyValuePairs,
  SpaceBetween,
} from '@cloudscape-design/components';

export function ApplicationDetails() {
  const { accountNo, applicationId } = useParams();
  const { data, isLoading, error } = getApplication({
    account: accountNo,
    id: applicationId,
  });
  const [applicationModalVisible, setApplicationModalVisible] =
    useState<boolean>(false);
  const [applicationModalMode, setApplicationModalMode] = useState<
    'New' | 'Edit' | 'Delete'
  >('New');
  const [applicationModalValues, setApplicationModalValues] =
    useState<any>(null);

  const showApplicationModal = (
    mode: 'New' | 'Edit' | 'Delete',
    values: any,
  ) => {
    setApplicationModalValues(values);
    setApplicationModalMode(mode);
    setApplicationModalVisible(true);
  };

  const navigate = useNavigate();

  if (error && error.message.includes('does not exist')) {
    navigate('/404');
  }

  const actionHandler = (
    event: CustomEvent<ButtonDropdownProps.ItemClickDetails>,
  ) => {
    switch (event.detail.id) {
      case 'edit_application':
        if (!data) return;
        showApplicationModal('Edit', {
          account: accountNo,
          id: applicationId,
          name: data.name,
          description: data.description,
          s3ScriptFileLocation: data.s3ScriptFileLocation,
          roleArn: data.roleArn,
          roleExternalId: data.roleExternalId,
          version: data.version,
        });
        break;
      case 'delete_application':
        showApplicationModal('Delete', {
          account: accountNo,
          id: applicationId,
        });
        break;
    }
  };

  return (
    <div className="application-details">
      <div className="application-details__header">
        <h2>Application Details</h2>
        <div className="application-details__button-group">
          <PermissionAwareButtonDropdown
            variant="normal"
            onItemClick={(e) => actionHandler(e)}
            items={[
              {
                text: 'Edit application',
                id: 'edit_application',
                requiredPermission: 'ADMINISTER',
                actionDescription: 'edit applications',
              },
              {
                text: 'Delete application',
                id: 'delete_application',
                requiredPermission: 'ADMINISTER',
                actionDescription: 'delete applications',
              },
            ]}
          >
            Manage Application
          </PermissionAwareButtonDropdown>
        </div>
      </div>
      <SpaceBetween size="s">
        <Container>
          <KeyValuePairs
            columns={3}
            items={[
              { label: 'Name', value: data?.name || '-' },
              { label: 'Id', value: data?.id || '-' },
              {
                label: 'Access IAM Role Name',
                value: data?.roleArn ? getRoleName(data.roleArn) : '-',
              },
              {
                label: 'Access IAM Role External Id',
                value: data?.roleExternalId || '-',
              },
              {
                label: 'User Script S3 Location',
                value: data?.s3ScriptFileLocation || '-',
              },
              { label: 'Description', value: data?.description || '-' },
              { label: 'Version', value: data?.version || '1' },
            ]}
          />
        </Container>

        <ModelVersionList />
      </SpaceBetween>

      <div className="profile-details">
        <Placeholder
          shapes={
            <>
              <rect y="0" width="90" height="25" />
              <rect x="30%" y="0" width="90" height="25" />
              <rect x="60%" y="0" width="90" height="25" />
              <rect x="80%" y="0" width="90" height="25" />
            </>
          }
          ready={!isLoading}
        >
          <ProfileDetails
            createdBy={data?.createdBy || ''}
            updatedBy={data?.updatedBy || ''}
            createdDate={data?.createdDate || ''}
            updatedDate={data?.updatedDate || ''}
          />
        </Placeholder>
      </div>
      {accountNo && (
        <ApplicationModal
          visible={applicationModalVisible}
          modalMode={applicationModalMode}
          values={applicationModalValues}
          onClose={() => {
            setApplicationModalVisible(false);
          }}
          account={accountNo}
          redirectLocation={
            applicationModalMode === 'Delete'
              ? `/accounts/${accountNo}`
              : undefined
          }
        />
      )}
    </div>
  );
}
